import {useRef, useState} from 'react'
import {Banner, Checkbox, FormControl, TextInput, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {EmojiAutocomplete} from '@github-ui/emoji-autocomplete'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useCreateListMutation} from '../hooks/use-create-list-mutation'
import type {StarState} from '../types'

const NAME_MAX_LENGTH = 32
const DESCRIPTION_MAX_LENGTH = 160

export interface CreateListDialogProps {
  createPath: string
  listsApiPath: string
  onClose: () => void
  repositoryId?: string
  starCount: number
  onStarStateChange: (next: StarState) => void
}

export function CreateListDialog({
  createPath,
  listsApiPath,
  onClose,
  repositoryId,
  starCount,
  onStarStateChange,
}: CreateListDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const trimmedName = name.trim()
  const isValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX_LENGTH

  const createListMutation = useCreateListMutation({
    createPath,
    listsApiPath,
    starCount,
    onStarStateChange,
    onClose,
  })

  const handleCreate = () => {
    if (!isValid || createListMutation.isPending) return
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: 'click:star_list_create_submit',
      label: `location:header;ref_cta:create_list_dialog;is_private:${isPrivate}`,
    })
    createListMutation.mutate({
      name: trimmedName,
      description: description.trim(),
      isPrivate,
      repositoryId,
    })
  }

  const errorMessage = createListMutation.error?.message ?? null

  return (
    <Dialog
      onClose={onClose}
      title="Create list"
      width="large"
      initialFocusRef={nameRef}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onClose},
        {
          buttonType: 'primary',
          content: 'Create',
          onClick: handleCreate,
          disabled: !isValid || createListMutation.isPending,
        },
      ]}
    >
      <p className="color-fg-muted tmp-mb-3">Create a list to organize your starred repositories.</p>
      {errorMessage && (
        <Banner variant="critical" title="Error" hideTitle description={errorMessage} className="tmp-mb-3" />
      )}
      <FormControl>
        <FormControl.Label>Name</FormControl.Label>
        <EmojiAutocomplete fullWidth>
          <TextInput
            ref={nameRef}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="⭐️ Name this list"
            maxLength={NAME_MAX_LENGTH}
            characterLimit={NAME_MAX_LENGTH}
            block
          />
        </EmojiAutocomplete>
      </FormControl>
      <FormControl className="tmp-mt-3">
        <FormControl.Label>Description</FormControl.Label>
        <EmojiAutocomplete fullWidth>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Write a description"
            maxLength={DESCRIPTION_MAX_LENGTH}
            characterLimit={DESCRIPTION_MAX_LENGTH}
            block
            resize="vertical"
            rows={3}
          />
        </EmojiAutocomplete>
      </FormControl>
      <FormControl className="tmp-mt-3">
        <Checkbox checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} />
        <FormControl.Label>Private</FormControl.Label>
        <FormControl.Caption>Enabling this makes the list visible only to you.</FormControl.Caption>
      </FormControl>
      <p className="color-fg-muted f6 tmp-mt-3">Tip: type : to add emoji to the name or description.</p>
    </Dialog>
  )
}
